// Validação do grafo antes de publicar — Fase 10.
//
// Função pura, sem React e sem Supabase: é o que `node --test` exercita
// (__tests__/validarFluxo.test.ts). Mesma disciplina da Fase 9 — a regra vem
// antes da tela, porque é na regra que o erro é barato.
//
// POR QUE ISTO EXISTE, e não é capricho de formulário: o motor da Fase 9
// interrompe o run quando encontra passo mal montado, com motivo legível. Só
// que ele descobre isso NA CONVERSA DO CLIENTE, uma conversa por vez, depois de
// publicado. A validação aqui move o mesmo erro para o instante em que quem
// montou ainda está olhando o fluxo — e o custo de errar cai de "um cliente
// atendido pela metade" para "um aviso vermelho na tela".
//
// A separação erro/aviso não é cosmética: ERRO impede publicar, AVISO não. Por
// isso a régua é estreita — só vira erro o que o motor recusaria de fato, ou o
// que deixaria o fluxo parado sem ninguém entender por quê.

import type { CampoConfig } from './catalogoNos.ts';
import { CAMPOS_INTERPOLAVEIS, OPERADORES, passo } from './catalogoNos.ts';
import type { Grafo } from './tipos.ts';

export interface Problema {
  nivel: 'erro' | 'aviso';
  /** `node_key` do culpado, ou null quando o problema é do fluxo inteiro. */
  noKey: string | null;
  mensagem: string;
}

/** Teto do atraso aceito por `inbox_fila_enfileirar_com_atraso`. */
export const ESPERA_MAXIMA_SEGUNDOS = 3600;

const RE_INTERPOLACAO = /\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g;

/**
 * Hosts que o worker recusa na hora de chamar o webhook.
 *
 * Repetido aqui de propósito: a proteção de verdade é a do worker, que é quem
 * sai para a rede. Esta cópia só antecipa o erro para o editor — sem ela, o
 * fluxo publicaria e falharia depois, com o motivo escondido na trilha do run.
 */
function hostInterno(url: URL): boolean {
  const h = url.hostname.toLowerCase();
  if (h === 'localhost' || h.endsWith('.local') || h.endsWith('.internal')) return true;
  if (h === '169.254.169.254') return true; // metadados de nuvem
  if (/^127\./.test(h) || /^10\./.test(h) || /^192\.168\./.test(h)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(h)) return true;
  return false;
}

/** Campos `{{...}}` presentes no texto que não estão na lista fechada. */
export function camposRecusados(texto: string): string[] {
  const fora: string[] = [];
  for (const achado of String(texto ?? '').matchAll(RE_INTERPOLACAO)) {
    const campo = achado[1];
    if (!(CAMPOS_INTERPOLAVEIS as readonly string[]).includes(campo) && !fora.includes(campo)) {
      fora.push(campo);
    }
  }
  return fora;
}

function vazio(v: unknown): boolean {
  return v === null || v === undefined || String(v).trim() === '';
}

function validarCampo(campo: CampoConfig, config: Record<string, unknown>, noKey: string): Problema[] {
  const problemas: Problema[] = [];
  const valor = config?.[campo.chave];

  if (campo.tipo === 'condicao') {
    const cond = valor as { campo?: string; operador?: string; valor?: unknown } | undefined;
    if (!cond || vazio(cond.campo)) {
      problemas.push({ nivel: 'erro', noKey, mensagem: 'A condição está sem o campo a comparar.' });
      return problemas;
    }
    const op = OPERADORES.find((o) => o.valor === cond.operador);
    if (!op) {
      // Operador fora da lista é recusado pelo motor (regra R5) — o run pararia
      // com "operador não suportado" no meio do atendimento.
      problemas.push({
        nivel: 'erro',
        noKey,
        mensagem: 'A condição usa um operador que o motor não conhece: "' + String(cond.operador ?? '') + '".',
      });
      return problemas;
    }
    if (op.precisaValor && vazio(cond.valor)) {
      problemas.push({
        nivel: 'erro',
        noKey,
        mensagem: 'A condição "' + op.rotulo + '" está sem valor de comparação.',
      });
    }
    return problemas;
  }

  if (campo.obrigatorio && vazio(valor)) {
    problemas.push({ nivel: 'erro', noKey, mensagem: 'O campo "' + campo.rotulo + '" está vazio.' });
    return problemas;
  }

  if (campo.tipo === 'numero' && !vazio(valor)) {
    const n = Number(valor);
    if (!Number.isFinite(n) || n <= 0) {
      problemas.push({
        nivel: 'erro',
        noKey,
        mensagem: '"' + campo.rotulo + '" precisa ser um número maior que zero.',
      });
    } else if (campo.chave === 'segundos' && n > ESPERA_MAXIMA_SEGUNDOS) {
      // A fila aceita no máximo 1h de atraso. Uma espera de 2h publicaria e
      // falharia no enfileiramento, longe daqui.
      problemas.push({
        nivel: 'erro',
        noKey,
        mensagem:
          'A espera máxima por passo é ' + ESPERA_MAXIMA_SEGUNDOS +
          's (1 hora). Para esperar mais, use passos de espera em sequência.',
      });
    }
  }

  if (campo.chave === 'url' && !vazio(valor)) {
    let url: URL | null = null;
    try {
      url = new URL(String(valor));
    } catch {
      url = null;
    }
    if (!url) {
      problemas.push({ nivel: 'erro', noKey, mensagem: 'O endereço do webhook não é uma URL válida.' });
    } else if (url.protocol !== 'https:') {
      problemas.push({ nivel: 'erro', noKey, mensagem: 'O webhook precisa ser https.' });
    } else if (hostInterno(url)) {
      problemas.push({
        nivel: 'erro',
        noKey,
        mensagem: 'O worker recusa endereço de rede interna — use um endereço público.',
      });
    }
  }

  if (campo.chave === 'texto' && !vazio(valor)) {
    const fora = camposRecusados(String(valor));
    if (fora.length > 0) {
      // Aviso, não erro: o motor não trava, ele troca por vazio e registra na
      // trilha. Bloquear a publicação por isso seria mais rígido que o motor.
      problemas.push({
        nivel: 'aviso',
        noKey,
        mensagem:
          'Estes campos saem vazios na mensagem, porque não estão na lista permitida: ' +
          fora.join(', ') + '.',
      });
    }
  }

  return problemas;
}

/** Mapa de saídas, usado pelas duas checagens que andam pelo grafo. */
function saidasPorNo(grafo: Grafo): Map<string, string[]> {
  const saidas = new Map<string, string[]>();
  for (const a of grafo.arestas) {
    const lista = saidas.get(a.de) ?? [];
    lista.push(a.para);
    saidas.set(a.de, lista);
  }
  return saidas;
}

/** Quem é alcançado a partir do passo inicial. Base da checagem de órfão. */
function alcancaveis(grafo: Grafo): Set<string> {
  const vistos = new Set<string>();
  if (!grafo.noInicial) return vistos;

  const saidas = saidasPorNo(grafo);
  const pilha = [grafo.noInicial];
  while (pilha.length > 0) {
    const atual = pilha.pop() as string;
    if (vistos.has(atual)) continue;
    vistos.add(atual);
    for (const proximo of saidas.get(atual) ?? []) pilha.push(proximo);
  }
  return vistos;
}

/**
 * Acha o primeiro ciclo e devolve o trecho que se fecha.
 *
 * Ciclo é ERRO, e não aviso, porque o motor não tem como distinguir "volta
 * proposital" de "engano": ele seguiria as arestas para sempre, e quem
 * seguraria seria o limite de mensagens por run — depois de já ter mandado
 * cinco mensagens para o cliente. Para repetir uma pergunta, o caminho é o
 * passo de aguardar resposta, que para e espera de verdade.
 */
function acharCiclo(grafo: Grafo): string[] | null {
  const saidas = saidasPorNo(grafo);

  const EM_ANDAMENTO = 1;
  const PRONTO = 2;
  const estado = new Map<string, number>();
  const caminho: string[] = [];

  function visitar(no: string): string[] | null {
    estado.set(no, EM_ANDAMENTO);
    caminho.push(no);
    for (const proximo of saidas.get(no) ?? []) {
      const e = estado.get(proximo);
      if (e === EM_ANDAMENTO) {
        // Devolve só o trecho que volta, não o caminho inteiro desde o início.
        const inicio = caminho.indexOf(proximo);
        return caminho.slice(inicio).concat(proximo);
      }
      if (e === undefined) {
        const achado = visitar(proximo);
        if (achado) return achado;
      }
    }
    caminho.pop();
    estado.set(no, PRONTO);
    return null;
  }

  for (const no of grafo.nos) {
    if (estado.get(no.node_key) === undefined) {
      const achado = visitar(no.node_key);
      if (achado) return achado;
    }
  }
  return null;
}

/**
 * Tudo que está errado no grafo, em ordem de leitura.
 *
 * Devolve lista, não boolean: o editor destaca cada nó culpado, e um "inválido"
 * sem dizer onde obrigaria a pessoa a caçar o problema no canvas.
 */
export function validarFluxo(grafo: Grafo): Problema[] {
  const problemas: Problema[] = [];
  const chaves = new Set(grafo.nos.map((n) => n.node_key));

  if (grafo.nos.length === 0) {
    problemas.push({ nivel: 'erro', noKey: null, mensagem: 'O fluxo não tem nenhum passo.' });
    return problemas;
  }

  // ─── Chave duplicada ──────────────────────────────────────────────────────
  const vistas = new Set<string>();
  for (const no of grafo.nos) {
    if (vistas.has(no.node_key)) {
      problemas.push({
        nivel: 'erro',
        noKey: no.node_key,
        mensagem:
          'Existe mais de um passo chamado "' + no.node_key +
          '". As ligações não saberiam a qual dos dois se referem.',
      });
    }
    vistas.add(no.node_key);
  }

  // ─── Passo inicial ────────────────────────────────────────────────────────
  if (!grafo.noInicial) {
    problemas.push({ nivel: 'erro', noKey: null, mensagem: 'Nenhum passo foi marcado como início do fluxo.' });
  } else if (!chaves.has(grafo.noInicial)) {
    problemas.push({
      nivel: 'erro',
      noKey: null,
      mensagem: 'O passo inicial "' + grafo.noInicial + '" não existe mais no fluxo.',
    });
  }

  // ─── Ligações ─────────────────────────────────────────────────────────────
  for (const a of grafo.arestas) {
    if (!chaves.has(a.de)) {
      problemas.push({
        nivel: 'erro',
        noKey: null,
        mensagem: 'Há uma ligação saindo de um passo que não existe ("' + a.de + '").',
      });
    }
    if (!chaves.has(a.para)) {
      problemas.push({
        nivel: 'erro',
        noKey: a.de,
        mensagem: 'Este passo liga para um passo que não existe ("' + a.para + '").',
      });
    }
  }

  // ─── Cada passo ───────────────────────────────────────────────────────────
  const chegam = alcancaveis(grafo);

  for (const no of grafo.nos) {
    const definicao = passo(no.tipo);
    if (!definicao) {
      problemas.push({
        nivel: 'erro',
        noKey: no.node_key,
        mensagem: 'Tipo de passo desconhecido: "' + no.tipo + '". O motor interromperia o fluxo aqui.',
      });
      continue;
    }

    for (const campo of definicao.campos) {
      problemas.push(...validarCampo(campo, no.config ?? {}, no.node_key));
    }

    const saindo = grafo.arestas.filter((a) => a.de === no.node_key);

    if (definicao.saida === 'nenhuma' && saindo.length > 0) {
      problemas.push({
        nivel: 'erro',
        noKey: no.node_key,
        mensagem: 'O passo "Fim" encerra o fluxo — ele não pode ligar para outro passo.',
      });
    }

    if (definicao.saida === 'unica') {
      if (saindo.length === 0) {
        problemas.push({
          nivel: 'erro',
          noKey: no.node_key,
          mensagem: 'Este passo não leva a lugar nenhum. Ligue-o ao próximo, ou a um passo "Fim".',
        });
      } else if (saindo.length > 1) {
        problemas.push({
          nivel: 'erro',
          noKey: no.node_key,
          mensagem: 'Este passo tem mais de uma saída, e não é uma condição — o motor não teria como escolher.',
        });
      }
    }

    if (definicao.saida === 'condicional') {
      const sim = saindo.filter((a) => a.quando === 'verdadeiro');
      const nao = saindo.filter((a) => a.quando === 'falso');
      if (sim.length === 0) {
        problemas.push({ nivel: 'erro', noKey: no.node_key, mensagem: 'A condição está sem o caminho do "sim".' });
      }
      if (nao.length === 0) {
        problemas.push({ nivel: 'erro', noKey: no.node_key, mensagem: 'A condição está sem o caminho do "não".' });
      }
      if (sim.length > 1) {
        problemas.push({ nivel: 'erro', noKey: no.node_key, mensagem: 'A condição tem dois caminhos para o "sim".' });
      }
      if (nao.length > 1) {
        problemas.push({ nivel: 'erro', noKey: no.node_key, mensagem: 'A condição tem dois caminhos para o "não".' });
      }
    }

    if (grafo.noInicial && chaves.has(grafo.noInicial) && !chegam.has(no.node_key)) {
      problemas.push({
        nivel: 'erro',
        noKey: no.node_key,
        mensagem: 'Nenhum caminho chega a este passo. Ele nunca seria executado.',
      });
    }
  }

  // ─── Ciclo ────────────────────────────────────────────────────────────────
  const ciclo = acharCiclo(grafo);
  if (ciclo) {
    problemas.push({
      nivel: 'erro',
      noKey: ciclo[0],
      mensagem:
        'O caminho volta para trás e se repete sem fim: ' + ciclo.join(' → ') +
        '. Para perguntar de novo, use um passo "Aguardar resposta".',
    });
  }

  // ─── Avisos de bom senso ──────────────────────────────────────────────────
  if (!grafo.nos.some((n) => n.tipo === 'fim')) {
    problemas.push({
      nivel: 'aviso',
      noKey: null,
      mensagem:
        'O fluxo não tem passo "Fim". Ele conclui ao acabar o caminho, mas o "Fim" deixa isso explícito para quem ler depois.',
    });
  }

  const envios = grafo.nos.filter((n) => n.tipo === 'enviar_mensagem').length;
  if (envios > 5) {
    // Cinco é o teto por run em `LIMITES_PADRAO`. Acima disso, o run é
    // interrompido no meio — e o cliente fica com a conversa pela metade.
    problemas.push({
      nivel: 'aviso',
      noKey: null,
      mensagem:
        'O fluxo tem ' + envios +
        ' passos de enviar mensagem, e o limite por conversa é 5. Um caminho que passe por todos seria interrompido no meio.',
    });
  }

  return problemas;
}

/** Só o que impede publicar. */
export function erros(problemas: Problema[]): Problema[] {
  return problemas.filter((p) => p.nivel === 'erro');
}

export function podePublicar(grafo: Grafo): boolean {
  return erros(validarFluxo(grafo)).length === 0;
}
